import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmail } from '../context/EmailContext';
import SafeIcon from '../common/SafeIcon';

const TemplateImporter = () => {
  const navigate = useNavigate();
  const { createTemplateImportJob, runTemplateImportJob, templateImportJobs } = useEmail();
  
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState(`project_id,name,purpose,tone,cta_style,subject_template,body_template
proj_demo,Intro Møte,Første kontakt,Normal,Book møte,"{{company_name}} – raskt spm","Hei {{first_name}},\n\nJeg så at dere vokser. Har dere vurdert vår løsning?\n\nMvh {{sender_name}}"
proj_demo,Oppfølging,Oppfølging,Kort,Svar med 1–2 linjer,"Re: {{company_name}}","Hei igjen,\n\nVille bare løfte denne. Tanker?\n\nMvh"`);
  
  const [options, setOptions] = useState({
    mode: 'Upsert',
    overwrite_body_template: true,
    overwrite_subject_template: true
  });

  const [currentJob, setCurrentJob] = useState(null);

  const handleValidate = async () => {
    const job = await createTemplateImportJob(csvText, options);
    setCurrentJob(job);
    setStep(2);
  };

  const handleExecute = async () => {
    if (!currentJob) return;
    await runTemplateImportJob(currentJob.job_id);
    setStep(3);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Template Importer V1</h1>
          <p className="text-slate-500 font-medium mt-1">Bulk-import av e-postmaler fra CSV.</p>
        </div>
        <button onClick={() => navigate('/email-templates')} className="text-slate-500 hover:text-slate-700 font-bold text-sm">
          Avbryt
        </button>
      </div>

      {/* Progress */}
      <div className="flex gap-4 mb-8">
        {[1, 2, 3].map(s => (
          <div key={s} className={`flex-1 h-2 rounded-full ${step >= s ? 'bg-primary-600' : 'bg-slate-200'}`} />
        ))}
      </div>

      {/* STEP 1: INPUT */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-left-4">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
              <label className="block text-sm font-black text-slate-900 uppercase tracking-widest mb-4">
                Lim inn CSV data
              </label>
              <textarea 
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                className="w-full h-[400px] p-4 bg-slate-900 text-slate-200 font-mono text-xs rounded-xl focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                placeholder="project_id,name,purpose,..."
              />
            </div>
          </div>
          
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-black text-slate-900">Innstillinger</h3>
              
              <div className="space-y-3">
                <div className="p-3 border rounded-xl">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Import Modus</label>
                  <select 
                    value={options.mode} 
                    onChange={e => setOptions({...options, mode: e.target.value})}
                    className="w-full p-2 bg-slate-50 rounded-lg text-sm font-bold outline-none"
                  >
                    <option value="Upsert">Oppdater eksisterende (Upsert)</option>
                    <option value="CreateOnly">Kun nye (Hopp over eksisterende)</option>
                  </select>
                </div>

                <label className="flex items-center justify-between p-3 border rounded-xl cursor-pointer hover:bg-slate-50">
                  <span className="text-sm font-bold text-slate-700">Overskriv Emnefelt</span>
                  <input type="checkbox" checked={options.overwrite_subject_template} onChange={e => setOptions({...options, overwrite_subject_template: e.target.checked})} className="w-5 h-5 rounded text-primary-600" />
                </label>
                
                <label className="flex items-center justify-between p-3 border rounded-xl cursor-pointer hover:bg-slate-50">
                  <span className="text-sm font-bold text-slate-700">Overskriv Brødtekst</span>
                  <input type="checkbox" checked={options.overwrite_body_template} onChange={e => setOptions({...options, overwrite_body_template: e.target.checked})} className="w-5 h-5 rounded text-primary-600" />
                </label>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <button onClick={handleValidate} disabled={!csvText} className="w-full py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50">
                  Valider CSV
                </button>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl text-xs text-blue-800">
              <p className="font-bold mb-2"><SafeIcon icon="ℹ️" /> Formatkrav:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>project_id, name (Påkrevd ID)</li>
                <li>subject_template, body_template</li>
                <li>purpose (Enum: Første kontakt...)</li>
                <li>tone (Enum: Kort, Normal...)</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: VALIDATION */}
      {step === 2 && currentJob && (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
          <div className="flex justify-between items-center bg-white p-6 rounded-[2rem] border border-slate-200">
            <div className="flex gap-8">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Totalt rader</p>
                <p className="text-2xl font-black text-slate-900">{currentJob.summary.rows_total}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Gyldige</p>
                <p className="text-2xl font-black text-emerald-600">{currentJob.summary.rows_valid}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Feil</p>
                <p className="text-2xl font-black text-rose-600">{currentJob.summary.rows_invalid}</p>
              </div>
            </div>
            
            <button 
              onClick={handleExecute} 
              disabled={currentJob.summary.rows_invalid > 0} 
              className="px-8 py-3 bg-primary-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-primary-700 disabled:bg-slate-300 shadow-lg shadow-primary-200"
            >
              Start Import 🚀
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-[2rem] overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Rad</th>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Navn</th>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Formål</th>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Tone</th>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {currentJob.rows.map((row, i) => (
                  <tr key={i} className={row.validation_status === 'FEIL' ? 'bg-rose-50' : 'hover:bg-slate-50'}>
                    <td className="px-6 py-4 font-mono text-slate-500">{row.row_no}</td>
                    <td className="px-6 py-4 font-bold text-slate-900">{row.name}</td>
                    <td className="px-6 py-4">{row.purpose}</td>
                    <td className="px-6 py-4">{row.tone}</td>
                    <td className="px-6 py-4">
                      {row.validation_status === 'OK' ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-xs"><SafeIcon icon="✅" /> OK</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-600 font-bold text-xs" title={row.validation_errors}><SafeIcon icon="❌" /> {row.validation_errors}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {currentJob.summary.rows_invalid > 0 && (
            <div className="text-center">
              <button onClick={() => setStep(1)} className="text-slate-500 font-bold hover:underline">Gå tilbake og rett feil i CSV</button>
            </div>
          )}
        </div>
      )}

      {/* STEP 3: RESULT */}
      {step === 3 && currentJob && (
        <div className="flex flex-col items-center justify-center py-20 animate-in zoom-in duration-300">
          <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mb-8 shadow-xl shadow-emerald-100">
            <SafeIcon icon="🎉" className="w-12 h-12 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-2">Import Fullført!</h2>
          <div className="flex gap-8 mb-8 text-center">
            <div>
              <p className="text-3xl font-black text-slate-900">{currentJob.summary.templates_created}</p>
              <p className="text-xs text-slate-500 uppercase tracking-widest">Opprettet</p>
            </div>
            <div>
              <p className="text-3xl font-black text-slate-900">{currentJob.summary.templates_updated}</p>
              <p className="text-xs text-slate-500 uppercase tracking-widest">Oppdatert</p>
            </div>
          </div>
          
          <div className="flex gap-4">
            <button onClick={() => navigate('/email-templates')} className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 shadow-lg">
              Gå til Maler
            </button>
            <button onClick={() => { setStep(1); setCsvText(''); }} className="px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-50">
              Ny Import
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateImporter;